import { useMemo, useState } from "react";
import { motion } from "motion/react";
import { Axe, Loader2, Plus, Scissors, Shovel } from "lucide-react";

import { getText } from "shared/lib";
import {
  TreeModuleEventTypes,
  type TreeDescriptor,
} from "@/components/treeModule/lib";

import { animationVariants } from "../lib";

import { type TreeModuleExpertResult } from "@/hooks";

import { Button, Separator } from "shared/components";
import {
  DiggingTreeList,
  PruningTreeList,
  RemovingTreeList,
} from "@/components/treeModule";
import { QuestionLoader } from "@/components/wizard/questionLoader";

export type DescribeTreesData = {
  removingTreeDescriptors: Array<TreeDescriptor>;
  pruningTreeDescriptors: Array<TreeDescriptor>;
  diggingTreeDescriptors: Array<TreeDescriptor>;
};

export interface DescribeTreesContainerProps {
  councilCode: string;
  digging: boolean;
  disabled?: boolean;
  nextButtonLabel?: string;
  onClickNext: (treeData: DescribeTreesData) => void;
  pruning: boolean;
  removing: boolean;
  showLoading?: boolean;
  submitting?: boolean;
  treeModuleExpertResult: TreeModuleExpertResult | undefined | null;
}

export function DescribeTreesContainer({
  councilCode,
  digging,
  disabled,
  nextButtonLabel,
  onClickNext,
  pruning,
  removing,
  showLoading,
  submitting,
  treeModuleExpertResult,
}: DescribeTreesContainerProps) {
  const [removingTreeDescriptors, setRemovingTreeDescriptors] = useState<
    Array<TreeDescriptor>
  >([]);

  const [pruningTreeDescriptors, setPruningTreeDescriptors] = useState<
    Array<TreeDescriptor>
  >([]);

  const [diggingTreeDescriptors, setDiggingTreeDescriptors] = useState<
    Array<TreeDescriptor>
  >([]);

  const [removingTreesHaveBeenDeleted, setRemovingTreesHaveBeenDeleted] =
    useState(false);
  const [pruningTreesHaveBeenDeleted, setPruningTreesHaveBeenDeleted] =
    useState(false);
  const [diggingTreesHaveBeenDeleted, setDiggingTreesHaveBeenDeleted] =
    useState(false);

  const justRemoving = removing && !pruning && !digging;

  const justPruning = !removing && pruning && !digging;

  const justDigging = !removing && !pruning && digging;

  const isValid = useMemo(() => {
    if (
      removing &&
      removingTreeDescriptors.length === 0 &&
      !removingTreesHaveBeenDeleted
    ) {
      return false;
    }

    if (
      pruning &&
      pruningTreeDescriptors.length === 0 &&
      !pruningTreesHaveBeenDeleted
    ) {
      return false;
    }

    if (
      digging &&
      diggingTreeDescriptors.length === 0 &&
      !diggingTreesHaveBeenDeleted
    ) {
      return false;
    }

    return true;
  }, [
    removing,
    removingTreeDescriptors,
    removingTreesHaveBeenDeleted,
    pruning,
    pruningTreeDescriptors,
    pruningTreesHaveBeenDeleted,
    digging,
    diggingTreeDescriptors,
    diggingTreesHaveBeenDeleted,
  ]);

  const handleClickNext = () => {
    const treeData: DescribeTreesData = {
      removingTreeDescriptors,
      pruningTreeDescriptors,
      diggingTreeDescriptors,
    };

    onClickNext(treeData);
  };

  const renderHeader = () => {
    let title = getText("Describe the trees");
    let subTitle = getText(
      "This helps us determine if you're exempt from requiring a permit or not.",
    );

    return (
      <div className="flex flex-col gap-1">
        <h2 className="text-textPrimary text-headingxl">{title}</h2>
        <p className="text-textSubdued text-bodylg">{subTitle}</p>
      </div>
    );
  };

  const renderFooter = () => {
    return (
      <motion.div
        variants={animationVariants}
        animate={["buttons"]}
        className="mt-3 flex justify-end md:mt-8"
      >
        <Button
          data-testid="next-button"
          disabled={submitting || showLoading || disabled || !isValid}
          onClick={handleClickNext}
        >
          {submitting || showLoading ? (
            <>
              <Loader2 className="mr-3 animate-spin" data-testid="submitting" />
            </>
          ) : null}
          {getText(nextButtonLabel || "Continue")}
        </Button>
      </motion.div>
    );
  };

  const renderOtherTreeActivities = () => {
    const otherActivities = {
      removing: !removing && removingTreeDescriptors.length === 0,
      pruning: !pruning && pruningTreeDescriptors.length === 0,
      digging: !digging && diggingTreeDescriptors.length === 0,
    };

    if (!Object.values(otherActivities).some(Boolean)) return false;

    return (
      <>
        <Separator orientation="horizontal" />

        <div className="flex flex-col gap-2">
          <p className="text-textPrimary text-bodylg">
            {getText("Any other tree-related activity?")}
          </p>

          <div className="flex gap-3">
            {otherActivities.removing ? (
              <Button
                className="text-bodymd h-min gap-1 px-0 py-0 underline"
                onClick={() => {
                  const customEvent = new CustomEvent(
                    TreeModuleEventTypes.AddRemovingTree,
                  );
                  document.dispatchEvent(customEvent);
                }}
                variant="link"
              >
                <Plus className="min-h-5 min-w-5" />{" "}
                {getText("Removing a tree")}
              </Button>
            ) : null}

            {otherActivities.pruning ? (
              <Button
                className="text-bodymd h-min gap-1 px-0 py-0 underline"
                onClick={() => {
                  const customEvent = new CustomEvent(
                    TreeModuleEventTypes.AddPruningTree,
                  );
                  document.dispatchEvent(customEvent);
                }}
                variant="link"
              >
                <Plus className="min-h-5 min-w-5" /> {getText("Pruning a tree")}
              </Button>
            ) : null}

            {otherActivities.digging ? (
              <Button
                className="text-bodymd h-min gap-1 px-0 py-0 underline"
                onClick={() => {
                  const customEvent = new CustomEvent(
                    TreeModuleEventTypes.AddDiggingTree,
                  );
                  document.dispatchEvent(customEvent);
                }}
                variant="link"
              >
                <Plus className="min-h-5 min-w-5" />{" "}
                {getText("Works within 15m of a tree")}
              </Button>
            ) : null}
          </div>
        </div>
      </>
    );
  };

  if (showLoading) {
    return <QuestionLoader />;
  }

  return (
    <div
      className="wizard-step-transition flex h-full w-full flex-col gap-5 md:w-[600px] md:max-w-[600px]"
      data-testid="step-container-describe-trees"
    >
      {renderHeader()}

      <div
        className={
          removing || removingTreeDescriptors.length > 0 ? "" : "hidden"
        }
      >
        <RemovingTreeList
          newTreeOptions={{
            distanceAboveGroundLevel:
              treeModuleExpertResult?.distance_above_ground_level.toString(),
            showTreeHeight: councilCode.toUpperCase() === "BAY",
            treeHeight: treeModuleExpertResult?.tree_height_trigger,
            useCircumference:
              treeModuleExpertResult?.measurement_method === "circumference",
          }}
          onChangeTreeDescriptors={setRemovingTreeDescriptors}
          onTreeDeleted={() => setRemovingTreesHaveBeenDeleted(true)}
          title={
            <>
              <Axe className="h-4 w-4" />
              {getText("Trees you're proposing to remove")}
            </>
          }
          treeDescriptors={removingTreeDescriptors}
          treesHaveBeenDeleted={removingTreesHaveBeenDeleted}
        />
      </div>

      <div
        className={pruning || pruningTreeDescriptors.length > 0 ? "" : "hidden"}
      >
        <PruningTreeList
          newTreeOptions={{
            distanceAboveGroundLevel:
              treeModuleExpertResult?.distance_above_ground_level.toString(),
            useCircumference:
              treeModuleExpertResult?.measurement_method === "circumference",
          }}
          onChangeTreeDescriptors={setPruningTreeDescriptors}
          onTreeDeleted={() => setPruningTreesHaveBeenDeleted(true)}
          title={
            <>
              <Scissors className="h-4 w-4" />
              {getText("Trees you're proposing to prune")}
            </>
          }
          treeDescriptors={pruningTreeDescriptors}
          treesHaveBeenDeleted={pruningTreesHaveBeenDeleted}
        />
      </div>

      <div
        className={digging || diggingTreeDescriptors.length > 0 ? "" : "hidden"}
      >
        <DiggingTreeList
          newTreeOptions={{
            distanceAboveGroundLevel:
              treeModuleExpertResult?.distance_above_ground_level.toString(),
            showTreeHeight: councilCode.toUpperCase() === "BAY",
            treeHeight: treeModuleExpertResult?.tree_height_trigger,
            useCircumference:
              treeModuleExpertResult?.measurement_method === "circumference",
          }}
          onChangeTreeDescriptors={setDiggingTreeDescriptors}
          onTreeDeleted={() => setDiggingTreesHaveBeenDeleted(true)}
          subTitle={getText("Including trees beyond the property boundary")}
          title={
            <>
              <Shovel className="h-4 w-4" />
              {getText(
                "Trees within 15 metres of proposed buildings and works",
              )}
            </>
          }
          treeDescriptors={diggingTreeDescriptors}
          treesHaveBeenDeleted={diggingTreesHaveBeenDeleted}
        />
      </div>

      {renderOtherTreeActivities()}

      {renderFooter()}
    </div>
  );
}
